import React, { useState, useEffect, useCallback } from 'react';
import { Coins, DollarSign, Plus, Minus } from 'lucide-react';
import { treasuryService } from '../../services';
import { LoadingPage } from '../../components/ui/Loading';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { formatGold, parseGoldInput } from '../../utils/goldNotation';

const GOLD_CATEGORY_LABELS = { CAIXA: 'Caixa', ADMINISTRATIVO: 'Administrativo' };

const ClubTreasuryPage = () => {
  const [activeTab, setActiveTab] = useState('gold');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [goldData, setGoldData] = useState({ balances: { CAIXA: 0, ADMINISTRATIVO: 0 }, transactions: [], total: 0 });
  const [realData, setRealData] = useState({ balance: 0, transactions: [], total: 0 });

  const [goldForm, setGoldForm] = useState({ category: 'CAIXA', type: 'CREDIT', amount: '', description: '' });
  const [realForm, setRealForm] = useState({ type: 'CREDIT', amount: '', description: '' });

  const [goldCategoryFilter, setGoldCategoryFilter] = useState('');
  const [goldPage, setGoldPage] = useState(1);
  const [realPage, setRealPage] = useState(1);

  const loadGold = useCallback(async (page = 1, category = '', append = false) => {
    const params = { page };
    if (category) params.category = category;
    const data = await treasuryService.getGoldExtract(params);
    setGoldData((prev) => ({
      ...data,
      transactions: append ? [...prev.transactions, ...data.transactions] : data.transactions,
    }));
  }, []);

  const loadReal = useCallback(async (page = 1, append = false) => {
    const data = await treasuryService.getRealExtract({ page });
    setRealData((prev) => ({
      ...data,
      transactions: append ? [...prev.transactions, ...data.transactions] : data.transactions,
    }));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([loadGold(1, ''), loadReal(1)]);
      } catch (err) {
        setError(err.response?.data?.error || 'Erro ao carregar o caixa do clube.');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadGold, loadReal]);

  const handleGoldCategoryFilterChange = async (category) => {
    setGoldCategoryFilter(category);
    setGoldPage(1);
    try {
      setError('');
      await loadGold(1, category);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao filtrar o extrato.');
    }
  };

  const handleLoadMoreGold = async () => {
    const nextPage = goldPage + 1;
    try {
      setError('');
      await loadGold(nextPage, goldCategoryFilter, true);
      setGoldPage(nextPage);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar mais lançamentos.');
    }
  };

  const handleLoadMoreReal = async () => {
    const nextPage = realPage + 1;
    try {
      setError('');
      await loadReal(nextPage, true);
      setRealPage(nextPage);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar mais lançamentos.');
    }
  };

  const handleGoldSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      parseGoldInput(goldForm.amount);
      await treasuryService.createGoldTransaction(goldForm);
      setGoldForm({ ...goldForm, amount: '', description: '' });
      setGoldPage(1);
      await loadGold(1, goldCategoryFilter);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao registrar lançamento.');
    } finally {
      setSaving(false);
    }
  };

  const handleRealSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      await treasuryService.createRealTransaction(realForm);
      setRealForm({ ...realForm, amount: '', description: '' });
      setRealPage(1);
      await loadReal(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao registrar lançamento.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingPage />;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
          🏦 Caixa do Clube
        </h1>
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => setActiveTab('gold')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${activeTab === 'gold' ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white' : 'text-gray-300 hover:text-white hover:bg-white/5 border border-white/10'}`}
          >
            <Coins className="w-4 h-4" /> Gold
          </button>
          <button
            onClick={() => setActiveTab('real')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${activeTab === 'real' ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white' : 'text-gray-300 hover:text-white hover:bg-white/5 border border-white/10'}`}
          >
            <DollarSign className="w-4 h-4" /> Real
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {activeTab === 'gold' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['CAIXA', 'ADMINISTRATIVO'].map((category) => (
              <div key={category} className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                <p className="text-gray-400 text-sm">{GOLD_CATEGORY_LABELS[category]}</p>
                <p className="text-3xl font-bold text-white" title={(goldData.balances[category] || 0).toLocaleString('pt-BR')}>{formatGold(goldData.balances[category] || 0)}</p>
              </div>
            ))}
          </div>

          <form onSubmit={handleGoldSubmit} className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4">
            <h2 className="text-xl font-bold text-white">Novo lançamento</h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <Select value={goldForm.category} onChange={(e) => setGoldForm({ ...goldForm, category: e.target.value })}>
                <option value="CAIXA">Caixa</option>
                <option value="ADMINISTRATIVO">Administrativo</option>
              </Select>
              <Select value={goldForm.type} onChange={(e) => setGoldForm({ ...goldForm, type: e.target.value })}>
                <option value="CREDIT">Entrada</option>
                <option value="DEBIT">Saída</option>
              </Select>
              <Input
                placeholder="Valor (ex: 500k)"
                value={goldForm.amount}
                onChange={(e) => setGoldForm({ ...goldForm, amount: e.target.value })}
                variant="dark"
                required
              />
              <Input
                placeholder="Descrição"
                value={goldForm.description}
                onChange={(e) => setGoldForm({ ...goldForm, description: e.target.value })}
                variant="dark"
                required
              />
            </div>
            <Button type="submit" loading={saving}>
              {goldForm.type === 'CREDIT' ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />} Lançar
            </Button>
          </form>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <h2 className="text-xl font-bold text-white">Extrato</h2>
              <div className="w-full sm:w-56">
                <Select
                  value={goldCategoryFilter}
                  onChange={(e) => handleGoldCategoryFilterChange(e.target.value)}
                >
                  <option value="">Todas</option>
                  <option value="CAIXA">Caixa</option>
                  <option value="ADMINISTRATIVO">Administrativo</option>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              {goldData.transactions.map((t) => (
                <div key={t.id} className="flex justify-between items-center p-3 rounded-xl bg-white/5 border border-white/10">
                  <div>
                    <p className="text-white">{t.description}</p>
                    <p className="text-gray-400 text-xs">{GOLD_CATEGORY_LABELS[t.category]} • {new Date(t.createdAt).toLocaleString('pt-BR')}</p>
                  </div>
                  <p className={t.type === 'CREDIT' ? 'text-green-400 font-bold' : 'text-red-400 font-bold'} title={Number(t.amount).toLocaleString('pt-BR')}>
                    {t.type === 'CREDIT' ? '+' : '-'}{formatGold(t.amount)}
                  </p>
                </div>
              ))}
            </div>
            {goldData.transactions.length < (goldData.total || 0) && (
              <div className="flex justify-center mt-4">
                <Button size="sm" variant="outline" onClick={handleLoadMoreGold}>
                  Carregar mais
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'real' && (
        <>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <p className="text-gray-400 text-sm">Saldo em Real</p>
            <p className="text-3xl font-bold text-white">
              {(realData.balance || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </div>

          <form onSubmit={handleRealSubmit} className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4">
            <h2 className="text-xl font-bold text-white">Novo lançamento</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Select value={realForm.type} onChange={(e) => setRealForm({ ...realForm, type: e.target.value })}>
                <option value="CREDIT">Entrada</option>
                <option value="DEBIT">Saída</option>
              </Select>
              <Input
                type="number"
                step="0.01"
                placeholder="Valor (R$)"
                value={realForm.amount}
                onChange={(e) => setRealForm({ ...realForm, amount: e.target.value })}
                variant="dark"
                required
              />
              <Input
                placeholder="Descrição"
                value={realForm.description}
                onChange={(e) => setRealForm({ ...realForm, description: e.target.value })}
                variant="dark"
                required
              />
            </div>
            <Button type="submit" loading={saving}>
              {realForm.type === 'CREDIT' ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />} Lançar
            </Button>
          </form>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <h2 className="text-xl font-bold text-white mb-4">Extrato</h2>
            <div className="space-y-2">
              {realData.transactions.map((t) => (
                <div key={t.id} className="flex justify-between items-center p-3 rounded-xl bg-white/5 border border-white/10">
                  <div>
                    <p className="text-white">{t.description}</p>
                    <p className="text-gray-400 text-xs">{new Date(t.createdAt).toLocaleString('pt-BR')}</p>
                  </div>
                  <p className={t.type === 'CREDIT' ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                    {t.type === 'CREDIT' ? '+' : '-'}{Number(t.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </p>
                </div>
              ))}
            </div>
            {realData.transactions.length < (realData.total || 0) && (
              <div className="flex justify-center mt-4">
                <Button size="sm" variant="outline" onClick={handleLoadMoreReal}>
                  Carregar mais
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ClubTreasuryPage;
